import { useSsoPageChocoUserQuery } from '../../../generated';

export interface SsoChocoUserStats {
  name?: string;
  phone?: string;
  locale?: string;
  loginMethod?: string;
  /** Epoch millis, as the User type returns it. */
  createdAt?: number;
  pending: boolean;
  linkedSupplierId?: string;
  linkedSupplierName?: string;
  linkedBuyerCount: number;
}

export interface UseSsoChocoUserInput {
  /** The Choco user id from the SSO record, absent when no account is linked. */
  chocoUserId?: string;
}

export interface UseSsoChocoUserResult {
  stats: SsoChocoUserStats | null;
  isLoading: boolean;
  /** Set when the lookup failed. Distinct from the account not existing. */
  error: Error | null;
  /** True only when the lookup succeeded and returned no account. */
  isMissing: boolean;
  refetch: () => void;
}

export const useSsoChocoUser = ({
  chocoUserId,
}: UseSsoChocoUserInput): UseSsoChocoUserResult => {
  const { data, loading, error, refetch } = useSsoPageChocoUserQuery({
    variables: { userId: chocoUserId ?? '' },
    skip: !chocoUserId,
    fetchPolicy: 'cache-first',
  });

  const user = data?.user;

  return {
    stats: user
      ? {
          name: user.name ?? undefined,
          phone: user.phone ?? undefined,
          locale: user.locale ?? undefined,
          loginMethod: user.loginMethod ?? undefined,
          createdAt: user.createdAt ?? undefined,
          pending: !!user.pending,
          linkedSupplierId: user.linkedSupplier?.id ?? undefined,
          linkedSupplierName: user.linkedSupplier?.name ?? undefined,
          linkedBuyerCount: user.buyers?.edges?.length ?? 0,
        }
      : null,
    isLoading: loading,
    error: error ?? null,
    // Only once the lookup succeeded: a failed query says nothing about
    // whether the account exists.
    isMissing: !!chocoUserId && !loading && !error && !user,
    refetch: () => {
      refetch();
    },
  };
};
